// src/components/MobileProtectedRoute.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import api, { AUTH_REQUEST_TIMEOUT_MS } from '../services/api';
import { 
  determineDestinationRoute, 
  shouldRedirect, 
  validateTokenScope,
  MOBILE_ROUTE_CONFIG 
} from '../config/mobileRoutes';
import {
  getTokenValidationErrorType,
  isCanceledRequest,
  isRetryableTokenValidationError,
} from '../utils/tokenValidationError';
import { safeLocalStorage } from '../utils/safeLocalStorage';

const MobileProtectedRoute = ({ children }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();
    let isActive = true;
    const retryPath = `${location.pathname}${location.search}`;

    setIsValidating(true);
    setIsValid(false);
    validateTokenAccess({
      signal: abortController.signal,
      isActive: () => isActive,
      retryPath,
    });

    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [token, location.pathname]);

  // Verificar si la ruta actual está permitida para el tipo de token
  const isCurrentRouteAllowed = (tokenType) => {
    const currentPath = location.pathname;
    const config = MOBILE_ROUTE_CONFIG.TOKEN_DESTINATIONS[tokenType];
    
    if (!config) return false;
    
    // Verificar si es un entry point (estas rutas son válidas pero necesitan redirección)
    const isEntryPoint = MOBILE_ROUTE_CONFIG.ENTRY_POINT_PATHS.some(entryPath => {
      const pathPattern = entryPath.replace(':token', token);
      return currentPath === pathPattern;
    });
    
    if (isEntryPoint) {
      return true; // Los entry points son válidos, pero se redirigen después
    }
    
    // Verificar usando allowedRoutes
    if (config.allowedRoutes && config.allowedRoutes.length > 0) {
      return config.allowedRoutes.some(allowedRoute => {
        const routePattern = allowedRoute.replace(':token', token);
        return currentPath === routePattern;
      });
    }
    
    // Fallback: verificar basePath (para retrocompatibilidad)
    return currentPath.startsWith(`${config.basePath}/${token}`);
  };

  // Redirigir a la ruta correcta para el tipo de token
  const redirectToCorrectRoute = (tokenType) => {
    const config = MOBILE_ROUTE_CONFIG.TOKEN_DESTINATIONS[tokenType];
    if (config && config.requiresRedirect) {
      const correctPath = `${config.basePath}/${token}${config.targetPath}`;
      navigate(correctPath, { replace: true });
    }
  };

  const validateTokenAccess = async ({ signal, isActive, retryPath }) => {
    try {
      await performTokenValidation({ signal, isActive, retryPath });
    } catch (error) {
      if (!isActive() || isCanceledRequest(error)) {
        return;
      }
      console.error('Error validating token access:', error);
      handleTokenError(error, retryPath);
    }
  };

  const performTokenValidation = async ({ signal, isActive, retryPath }) => {
    try {
      const response = await api.post('/temporary-token/validate', { token }, {
        timeout: AUTH_REQUEST_TIMEOUT_MS,
        signal,
        skipUnauthorizedRedirect: true,
        networkRetry: true,
      });

      if (!isActive()) {
        return;
      }
      
      // Guardamos datos básicos en localStorage con prefijo temp_ para evitar conflicto con sesión del vendedor
      safeLocalStorage.setItem('temp_authToken', response.data.access_token);
      safeLocalStorage.setItem('temp_tokenScope', JSON.stringify(response.data.scope));
      safeLocalStorage.setItem('temp_tokenType', response.data.type);
      safeLocalStorage.setItem('temp_tokenLastValidation', Date.now().toString());
      
      const { type } = response.data;
      const tokenScope = response.data.scope || {};
      
      // Extraer y guardar IDs del scope
      const { sale_id, customer_id, purchase_request_id, branch_id } = tokenScope;
      if (sale_id) safeLocalStorage.setItem('temp_tokenSaleId', sale_id);
      if (customer_id) safeLocalStorage.setItem('temp_tokenCustomerId', customer_id);
      if (purchase_request_id) safeLocalStorage.setItem('temp_tokenPurchaseRequestId', purchase_request_id);
      if (branch_id) safeLocalStorage.setItem('temp_tokenBranchId', branch_id);

      if (!isActive()) {
        return;
      }
      
      // Validar scope
      if (!validateTokenScope(type, tokenScope)) {
        handleTokenError(new Error('Scope inválido para el tipo de token'), retryPath);
        return;
      }
      
      // Verificar redirección
      const currentPath = location.pathname;
      const fromStep = searchParams.get('from_step');
      
      if (shouldRedirect(currentPath, token, type)) {
        const expectedRoute = determineDestinationRoute(token, response.data.scope, type, fromStep);
        navigate(expectedRoute, { replace: true });
        return;
      }
      
      // Verificar permisos de ruta
      if (!isCurrentRouteAllowed(type)) {
        redirectToCorrectRoute(type);
        return;
      }
      
      setIsValid(true);
      setIsValidating(false);

    } catch (error) {
      if (!isActive() || isCanceledRequest(error)) {
        return;
      }
      console.error('Error performing token validation:', error);
      handleTokenError(error, retryPath);
    }
  };

  const handleTokenError = (error, retryPath) => {
    const errorType = getTokenValidationErrorType(error);
    if (!errorType) {
      return;
    }

    // Limpiar localStorage de tokens temporales (con prefijo temp_)
    safeLocalStorage.removeItem('temp_authToken');
    safeLocalStorage.removeItem('temp_tokenScope');
    safeLocalStorage.removeItem('temp_tokenType');
    safeLocalStorage.removeItem('temp_tokenSaleId');
    safeLocalStorage.removeItem('temp_tokenCustomerId');
    safeLocalStorage.removeItem('temp_tokenPurchaseRequestId');
    safeLocalStorage.removeItem('temp_tokenBranchId');
    safeLocalStorage.removeItem('temp_tokenLastValidation');

    setIsValidating(false);
    setIsValid(false);

    navigate(`/m/error/${errorType}`, {
      replace: true,
      state: isRetryableTokenValidationError(errorType) ? { retryPath } : undefined,
    });
  };

  // Mostrar spinner mientras validamos
  if (isValidating) {
    return (
      <div className="container-fluid vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Validando acceso...</span>
          </div>
          <p className="text-muted">Verificando permisos...</p>
        </div>
      </div>
    );
  }

  // Si la validación falla, no renderizar nada (ya redirigimos)
  if (!isValid) {
    return null;
  }

  // Si todo está bien, renderizar el componente hijo
  return children;
};

export default MobileProtectedRoute;